/* eslint-disable prettier/prettier */
// src/hooks/usePushNotifications.ts
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import {
  ActionPerformed,
  PushNotifications,
  PushNotificationSchema,
  Token
} from "@capacitor/push-notifications";
import {
  getToken as firebaseGetToken,
  onMessage as firebaseOnMessage,
  Messaging
} from "firebase/messaging";

import { getPath } from "helpers/convertFormData";
import { showSuccessMessage } from "helpers/toastMessage";
import { IActivityItem } from "models/Home";
import { uploadFirebaseToken } from "services/api/user";
import { RootState } from "../store";

const goDetails = async (data: IActivityItem) => {
  const navigate = useNavigate();
  const { account } = useSelector((state: RootState) => state.authState);
  const path = getPath(data, account);
  if (path) navigate(path);
};

const usePushNotifications = (messaging: Messaging | undefined) => {
  const { userToken } = useSelector((state: RootState) => state.authState);

  useEffect(() => {
    const initializePush = async () => {
      const TOKEN_STORAGE_KEY = "firebaseToken";

      const uploadTokenIfNeeded = async (token: string) => {
        const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (stored !== token) {
          const success = await uploadFirebaseToken(token);
          if (success) {
            localStorage.setItem(TOKEN_STORAGE_KEY, token);
          }
        }
      };

      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        // ─── NATIVE (iOS & Android) ──────────────────────────────
        let perm = await PushNotifications.checkPermissions();
        if (perm.receive !== "granted") {
          perm = await PushNotifications.requestPermissions();
        }
        if (perm.receive !== "granted") return;

        await PushNotifications.register();
        PushNotifications.addListener("registration", async (tok: Token) => {
          await uploadTokenIfNeeded(tok.value);
        });
        PushNotifications.addListener("registrationError", () => {});
        PushNotifications.addListener(
          "pushNotificationReceived",
          async (n: PushNotificationSchema) => {
            const { isActive } = await App.getState();
            if (isActive) showSuccessMessage(n.data.message);
          }
        );
        PushNotifications.addListener("pushNotificationActionPerformed", (a: ActionPerformed) =>
          goDetails(a.notification.data)
        );

        // also register with FCM plugin for background refresh
        try {
          const resp = await FirebaseMessaging.getToken();
          await uploadTokenIfNeeded(resp.token);
        } catch {
          // silently ignore errors
        }
        try {
          await FirebaseMessaging.deleteToken();
          const resp = await FirebaseMessaging.getToken();
          await uploadTokenIfNeeded(resp.token);
        } catch {
          // silently ignore errors
        }
      } else {
        // ─── WEB PLATFORM ────────────────────────────────────────
        // guard against Notification API missing
        if (
          typeof window === "undefined" ||
          typeof Notification === "undefined" ||
          typeof Notification.requestPermission !== "function"
        ) {
          // console.warn("ℹ️ Notifications API not available in this browser—skipping web push.");
          return;
        }

        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;

        if (messaging) {
          try {
            const sw = await navigator.serviceWorker.ready;
            const currentToken = await firebaseGetToken(messaging, {
              vapidKey: process.env.REACT_APP_VAPID!,
              serviceWorkerRegistration: sw
            });
            if (currentToken) {
              await uploadTokenIfNeeded(currentToken);
            }
          } catch {
            // silently fail token fetch
          }

          firebaseOnMessage(messaging, payload => {
            // show in-page notification
            const { title, body } = payload.notification || {};
            if (title && body && Notification.permission === "granted") {
              new Notification(title, {
                body,
                data: payload.data
              });
            }
          });
        }
      }
    };

    if (userToken) {
      initializePush();
    }
  }, [userToken, messaging]);
};

export default usePushNotifications;
